import type { StreamProviderId, StreamProviderType } from '@/types/streaming';
import { getProviderType, getProviderLabel } from '@/types/streaming';

// ── Provider Config (runtime state per provider instance) ──

export interface ProviderConfig {
  id: StreamProviderId;
  providerType: StreamProviderType;
  label: string;
  icon: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  streamUrl: string;
  fallbackUrl: string;
  format: string;
  bitrate: number;
  port: number;
  mount: string;
  lastValidated: string | null;
  validated: boolean | null;
  validating: boolean;
  active: boolean;
}

export interface StreamState {
  status: 'online' | 'offline' | 'reconectando' | 'verificando';
  currentProvider: StreamProviderId;
  currentProviderType: StreamProviderType;
  listeners: number;
  peakListeners: number;
  bitrate: number;
  format: string;
  uptime: string;
  lastConnection: string;
  streamUrl: string;
  startedAt: string | null;
}

export interface SocialStream {
  id: string;
  platform: 'facebook' | 'youtube';
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  activeColor: string;
  activeBg: string;
  channelName: string;
  pageName: string;
  connected: boolean;
  active: boolean;
  viewerCount: number;
  lastBroadcast: string;
  streamKey: string;
  rtmpUrl: string;
}

export interface StreamEvent {
  id: number;
  type: 'stream_start' | 'stream_end' | 'stream_error' | 'stream_reconnect' | 'facebook' | 'youtube' | 'url_validated' | 'provider_switch';
  title: string;
  description: string;
  timestamp: string;
  icon: string;
  color: string;
  bgColor: string;
}

const now = new Date();
const hoursAgo = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
const daysAgo = (d: number) => new Date(now.getTime() - d * 86400000).toISOString();

export const providers: ProviderConfig[] = [
  {
    id: 'icecast',
    providerType: getProviderType('icecast'),
    label: 'Icecast',
    icon: 'ri-server-line',
    description: 'Servidor de streaming estándar. Soporta AAC, MP3, OGG. El más usado en radio online.',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
    borderColor: 'border-cyan-300',
    streamUrl: 'https://stream.radiofrecuenciaweb.com:8000/live',
    fallbackUrl: 'https://stream2.radiofrecuenciaweb.com:8000/live',
    format: 'AAC',
    bitrate: 320,
    port: 8000,
    mount: '/live',
    lastValidated: '2026-06-19T08:30:00',
    validated: true,
    validating: false,
    active: true,
  },
  {
    id: 'shoutcast',
    providerType: getProviderType('shoutcast'),
    label: 'Shoutcast',
    icon: 'ri-broadcast-line',
    description: 'Servidor de streaming alternativo. Compatible con MP3 y AAC+. Buena alternativa.',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
    streamUrl: 'https://stream.radiofrecuenciaweb.com:9000/stream',
    fallbackUrl: '',
    format: 'MP3',
    bitrate: 192,
    port: 9000,
    mount: '/stream',
    lastValidated: '2026-06-18T14:00:00',
    validated: true,
    validating: false,
    active: false,
  },
  {
    id: 'centovacast',
    providerType: getProviderType('centovacast'),
    label: 'Centova Cast',
    icon: 'ri-dashboard-3-line',
    description: 'Panel de control con AutoDJ incluido, estadísticas y control total.',
    color: 'text-violet-600',
    bgColor: 'bg-violet-100',
    borderColor: 'border-violet-300',
    streamUrl: 'https://centova.radiofrecuenciaweb.com:2199/stream',
    fallbackUrl: '',
    format: 'AAC+',
    bitrate: 256,
    port: 2199,
    mount: '/stream',
    lastValidated: null,
    validated: null,
    validating: false,
    active: false,
  },
  {
    id: 'custom',
    providerType: getProviderType('custom'),
    label: 'Proveedor propio',
    icon: 'ri-global-line',
    description: 'Conectá cualquier servidor de streaming propio o de tu hosting. URL personalizada.',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-300',
    streamUrl: '',
    fallbackUrl: '',
    format: 'MP3',
    bitrate: 128,
    port: 0,
    mount: '',
    lastValidated: null,
    validated: null,
    validating: false,
    active: false,
  },
];

export const streamState: StreamState = {
  status: 'online',
  currentProvider: 'icecast',
  currentProviderType: getProviderType('icecast'),
  listeners: 1247,
  peakListeners: 1890,
  bitrate: 320,
  format: 'AAC',
  uptime: '4h 12m',
  lastConnection: '2026-06-19T08:30:00',
  streamUrl: 'https://stream.radiofrecuenciaweb.com:8000/live',
  startedAt: hoursAgo(4),
};

export const socialStreams: SocialStream[] = [
  {
    id: 'fb-live',
    platform: 'facebook',
    label: 'Facebook Live',
    icon: 'ri-facebook-circle-fill',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    activeColor: 'text-blue-700',
    activeBg: 'bg-blue-500',
    channelName: 'RadioFrecuenciaWeb',
    pageName: 'Radio RFW Oficial',
    connected: true,
    active: false,
    viewerCount: 342,
    lastBroadcast: '2026-06-14 10:15 CST',
    streamKey: 'fb_live_sk_a1b2c3d4',
    rtmpUrl: 'rtmps://live-api-s.facebook.com:443/rtmp',
  },
  {
    id: 'yt-live',
    platform: 'youtube',
    label: 'YouTube Live',
    icon: 'ri-youtube-fill',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    activeColor: 'text-red-700',
    activeBg: 'bg-red-500',
    channelName: 'RadioFrecuenciaWeb TV',
    pageName: '',
    connected: true,
    active: false,
    viewerCount: 587,
    lastBroadcast: '2026-06-14 10:16 CST',
    streamKey: 'yt_live_sk_x7y8z9w0',
    rtmpUrl: 'rtmp://a.rtmp.youtube.com/live2',
  },
];

export const streamEvents: StreamEvent[] = [
  {
    id: 1,
    type: 'stream_start',
    title: 'Stream iniciado — Servidor principal',
    description: 'Conexión establecida con el servidor principal. AAC 320kbps estéreo. 1247 oyentes actuales.',
    timestamp: hoursAgo(4),
    icon: 'ri-play-circle-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  {
    id: 2,
    type: 'url_validated',
    title: 'URL validada — Servidor principal',
    description: 'Validación de URL exitosa. El servidor respondió correctamente con 320kbps y codec AAC.',
    timestamp: hoursAgo(4.1),
    icon: 'ri-check-double-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
  },
  {
    id: 3,
    type: 'facebook',
    title: 'Facebook Live activado',
    description: 'Retransmisión iniciada hacia Radio RFW Oficial. 342 espectadores alcanzados durante la sesión.',
    timestamp: hoursAgo(14),
    icon: 'ri-facebook-circle-fill',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    id: 4,
    type: 'youtube',
    title: 'YouTube Live activado',
    description: 'Retransmisión iniciada hacia RadioFrecuenciaWeb TV. 587 espectadores simultáneos.',
    timestamp: hoursAgo(14),
    icon: 'ri-youtube-fill',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  {
    id: 5,
    type: 'stream_error',
    title: 'Error de conexión — Timeout',
    description: 'El servidor no respondió por 15 segundos. El sistema intentó reconectar automáticamente.',
    timestamp: hoursAgo(28),
    icon: 'ri-error-warning-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
  },
  {
    id: 6,
    type: 'stream_reconnect',
    title: 'Reconexión exitosa',
    description: 'Conexión restablecida con el servidor. Buffer inicial completado. Sin pérdida de oyentes.',
    timestamp: hoursAgo(28),
    icon: 'ri-refresh-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    id: 7,
    type: 'stream_end',
    title: 'Stream finalizado',
    description: 'Transmisión del 18 de junio finalizada correctamente. Duración total: 8h 30m. Pico de 2103 oyentes.',
    timestamp: hoursAgo(22),
    icon: 'ri-stop-circle-line',
    color: 'text-foreground-600',
    bgColor: 'bg-foreground-100',
  },
  {
    id: 8,
    type: 'stream_start',
    title: 'Stream iniciado — Servidor secundario',
    description: 'Conexión establecida con el servidor secundario. MP3 192kbps estéreo. 876 oyentes.',
    timestamp: hoursAgo(30),
    icon: 'ri-play-circle-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  {
    id: 9,
    type: 'provider_switch',
    title: 'Cambio de proveedor — Secundario → Principal',
    description: 'Se restauró el proveedor principal. La transición fue automática sin interrupción del stream.',
    timestamp: hoursAgo(28),
    icon: 'ri-arrow-left-right-line',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  {
    id: 10,
    type: 'url_validated',
    title: 'URL validada — Servidor secundario',
    description: 'Validación exitosa del servidor secundario. MP3 192kbps, latencia 1.2s.',
    timestamp: daysAgo(1),
    icon: 'ri-check-double-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    id: 11,
    type: 'stream_end',
    title: 'Stream finalizado',
    description: 'Transmisión del 17 de junio finalizada. Duración total: 6h 15m. Pico de 1547 oyentes.',
    timestamp: hoursAgo(48),
    icon: 'ri-stop-circle-line',
    color: 'text-foreground-600',
    bgColor: 'bg-foreground-100',
  },
  {
    id: 12,
    type: 'facebook',
    title: 'Facebook Live — Prueba de conexión',
    description: 'Prueba de retransmisión a Facebook completada. El streaming se visualizó correctamente en la página.',
    timestamp: daysAgo(3),
    icon: 'ri-facebook-circle-fill',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
];